import {
  getDesignPreference,
  getHeightPreference,
  getStrebenPreference,
  inferEtagenbettHeight,
  inferDesignLine,
  candidateMatchesStrebenPreference,
  lineMatchRank,
  preferLineMatches,
} from "./candidateLine";
import {
  coerceStringArray,
  colorTokensMatch,
  inferSizeFromText,
  normalizeAgeTokens,
  normalizeSize,
  normalizeToken,
  resolveCandidateBedType,
  resolveCandidateWood,
  variantColorToToken,
} from "./normalize";
import type {
  BeraterAnswer,
  BeraterAttribute,
  BeraterCandidate,
  BeraterData,
  BeraterRecommendation,
  BeraterStepData,
} from "./types";

const HARD_NON_RELAXABLE: BeraterAttribute[] = ["age", "material", "wood"];
// CMS may still mark Holz steps soft; wood is always a hard pool filter once answered.
const ALWAYS_HARD_ATTRIBUTES: BeraterAttribute[] = ["wood"];
// color is resolved on the variant; pool filtering uses attrs.colors from Farbe options
const NON_MATCHING_ATTRIBUTES: BeraterAttribute[] = ["accessories", "none", "color"];
const RELAXABLE_ATTRIBUTES: BeraterAttribute[] = ["bed_type", "size"];
const RELAXABLE_BY_TOKEN = new Map<string, BeraterAttribute>([
  ["bed_type", "bed_type"],
  ["bedtype", "bed_type"],
  ["size", "size"],
]);
const JUGEND_PRIMARY_BED_TYPES = new Set(["jugendbett", "einzelbett"]);

const prepareCandidates = (candidates: BeraterCandidate[]): BeraterCandidate[] =>
  candidates.map((candidate) => {
    const age = normalizeAgeTokens(candidate.attrs.age, candidate.tags ?? []);
    const sizeRaw = coerceStringArray(candidate.attrs.sizeRaw);
    const colorRaw = coerceStringArray(candidate.attrs.colorRaw);
    const colors = [...new Set(colorRaw.map(variantColorToToken).filter(Boolean))];
    const wood = resolveCandidateWood(candidate.handle, candidate.title, candidate.attrs.wood);
    const bed_type = resolveCandidateBedType(candidate.handle, candidate.title, candidate.attrs.bed_type);
    const sizes = [...new Set([...sizeRaw.map(normalizeSize), ...inferSizeFromText(candidate.handle, candidate.title)].filter(Boolean))];
    return {
      ...candidate,
      attrs: {
        ...candidate.attrs,
        age: age.length ? age : null,
        wood,
        bed_type,
        sizes,
        colors,
      },
    };
  });

const candidateMatchesWood = (candidate: BeraterCandidate, wantTokens: string[]): boolean => {
  const wood = candidate.attrs.wood;
  if (!wood || !wantTokens.length) return false;
  return wantTokens.includes(normalizeToken(wood));
};

/** Prefer products that match the selected Holzart when any do. */
const preferWoodMatches = (pool: BeraterCandidate[], answers: BeraterAnswer[]): BeraterCandidate[] => {
  const wantWoods = getAnswerValues(answers, "wood");
  if (!wantWoods.length) return pool;

  const matching = pool.filter((candidate) => candidateMatchesWood(candidate, wantWoods));
  return matching.length ? matching : pool;
};

const woodMatchRank = (candidate: BeraterCandidate, wantTokens: string[]): number => {
  if (!wantTokens.length) return 0;
  return candidateMatchesWood(candidate, wantTokens) ? 2 : 0;
};

const candidateMatchesColor = (candidate: BeraterCandidate, wantTokens: string[]): boolean => {
  const colorRaws = coerceStringArray(candidate.attrs.colorRaw);
  if (!colorRaws.length || !wantTokens.length) return false;
  return wantTokens.some((want) => colorRaws.some((raw) => colorTokensMatch(want, raw)));
};

const candidateHasColorOptions = (candidate: BeraterCandidate): boolean =>
  coerceStringArray(candidate.attrs.colorRaw).length > 0 || (candidate.attrs.colors ?? []).length > 0;

/** Prefer products that carry the selected Farbe; natur-only parents stay eligible as fallback. */
const preferColorMatches = (pool: BeraterCandidate[], answers: BeraterAnswer[]): BeraterCandidate[] => {
  const wantColors = getAnswerValues(answers, "color");
  if (!wantColors.length) return pool;

  const matching = pool.filter((candidate) => candidateMatchesColor(candidate, wantColors));
  if (matching.length) return matching;

  const withoutColorOption = pool.filter((candidate) => !candidateHasColorOptions(candidate));
  return withoutColorOption.length ? withoutColorOption : pool;
};

const colorMatchRank = (candidate: BeraterCandidate, wantTokens: string[]): number => {
  if (!wantTokens.length) return 0;
  if (candidateMatchesColor(candidate, wantTokens)) return 2;
  if (!candidateHasColorOptions(candidate)) return 1;
  return 0;
};

/** Ehrenkind often splits color into separate parent handles (…-weiss, …-hellgrau). */
const colorHandleRank = (candidate: BeraterCandidate, wantTokens: string[]): number => {
  const handle = normalizeToken(candidate.handle);
  for (const want of wantTokens) {
    const token = normalizeToken(want);
    if (token === "naturweiss") {
      if (handle.includes("naturweiss")) return 1;
      continue;
    }
    if (token === "weiss") {
      if (handle.includes("naturweiss")) continue;
      if (handle.includes("weiss")) return 1;
      continue;
    }
    if (token && handle.includes(token)) return 1;
  }
  return 0;
};

const candidateMatchesLinePreference = (candidate: BeraterCandidate, answers: BeraterAnswer[]): boolean => {
  const design = getDesignPreference(answers);
  if (design) return inferDesignLine(candidate.handle) === design;
  if (getStrebenPreference(answers)) return candidateMatchesStrebenPreference(candidate, answers);
  const height = getHeightPreference(answers);
  if (height) return inferEtagenbettHeight(candidate.handle) === height;
  return true;
};

/** Intersect color + wood + line when all apply; line wins on conflict (Streben/Höhe comes after Farbe in the flow). */
const applyPreferenceFilters = (pool: BeraterCandidate[], answers: BeraterAnswer[]): BeraterCandidate[] => {
  const wantColors = getAnswerValues(answers, "color");
  const wantWoods = getAnswerValues(answers, "wood");
  const hasColor = wantColors.length > 0;
  const hasWood = wantWoods.length > 0;
  const hasDesign = getDesignPreference(answers) !== null;
  const hasLine = hasDesign || getStrebenPreference(answers) !== null || getHeightPreference(answers) !== null;

  const both = pool.filter(
    (candidate) =>
      (!hasColor || candidateMatchesColor(candidate, wantColors)) &&
      (!hasWood || candidateMatchesWood(candidate, wantWoods)) &&
      candidateMatchesLinePreference(candidate, answers),
  );
  if (both.length) return both;

  if (hasLine) {
    const lineOnly = preferLineMatches(pool, answers);
    // Design is exclusive (Plano vs Nova) — don't fall back to cheapest Nova.
    if (lineOnly.length || hasDesign) return lineOnly;
  }

  if (hasColor) {
    const colorOnly = preferColorMatches(pool, answers);
    if (colorOnly.length) return colorOnly;
  }

  if (hasWood) return preferWoodMatches(pool, answers);

  return pool;
};

const getAnswerValues = (answers: BeraterAnswer[], attribute: BeraterAttribute): string[] => {
  const values: string[] = [];
  for (const answer of answers) {
    if (answer.attribute !== attribute) continue;
    for (const value of answer.values) {
      values.push(normalizeToken(value));
    }
  }
  return values;
};

const isJugendOnlyAge = (answers: BeraterAnswer[]): boolean => {
  const ages = getAnswerValues(answers, "age");
  return ages.length === 1 && ages[0] === "jugendliche";
};

const candidateMatchesExactSize = (candidate: BeraterCandidate, wantSizes: string[]): boolean => {
  const sizes = candidate.attrs.sizes ?? [];
  if (!sizes.length || !wantSizes.length) return false;
  return wantSizes.some((value) => sizes.includes(normalizeSize(value)));
};

/** Drop fixed-dimension wildcards (Etagenbett) when another product lists the chosen Größe. */
const preferExactSizeMatches = (pool: BeraterCandidate[], answers: BeraterAnswer[]): BeraterCandidate[] => {
  const wantSizes = getAnswerValues(answers, "size");
  if (!wantSizes.length) return pool;

  const exact = pool.filter((candidate) => candidateMatchesExactSize(candidate, wantSizes));
  return exact.length ? exact : pool;
};

/** Jugend path has no Bettart step — keep Etagenbett/Kinderbett out when Jugendbetten exist. */
const preferJugendPrimaryBedTypes = (pool: BeraterCandidate[], answers: BeraterAnswer[]): BeraterCandidate[] => {
  if (!isJugendOnlyAge(answers) || getAnswerValues(answers, "bed_type").length) return pool;

  const primary = pool.filter((candidate) =>
    JUGEND_PRIMARY_BED_TYPES.has(normalizeToken(candidate.attrs.bed_type ?? "")),
  );
  return primary.length ? primary : pool;
};

const applyContextualFilters = (
  pool: BeraterCandidate[],
  answers: BeraterAnswer[],
  relaxedAttributes: BeraterAttribute[],
): BeraterCandidate[] => {
  let result = pool;
  if (!relaxedAttributes.includes("size")) {
    result = preferExactSizeMatches(result, answers);
  }
  if (!relaxedAttributes.includes("bed_type")) {
    result = preferJugendPrimaryBedTypes(result, answers);
  }
  return result;
};

const sizeMatchRank = (candidate: BeraterCandidate, wantSizes: string[]): number => {
  if (!wantSizes.length) return 0;
  return candidateMatchesExactSize(candidate, wantSizes) ? 2 : 0;
};

const attributeMatches = (candidate: BeraterCandidate, attribute: BeraterAttribute, values: string[]): boolean => {
  switch (attribute) {
    case "age": {
      const ages = candidate.attrs.age ?? [];
      return values.some((value) => ages.includes(value));
    }
    case "size": {
      const sizes = candidate.attrs.sizes ?? [];
      // No size option on the parent product (e.g. Etagenbett, fixed-dimension Familienbett):
      // treat as wildcard — age hard-filter still prevents wrong life-stage matches.
      if (!sizes.length) return true;
      return values.some((value) => sizes.includes(normalizeSize(value)));
    }
    case "bed_type":
      return values.includes(normalizeToken(candidate.attrs.bed_type ?? ""));
    case "material":
      return values.includes(normalizeToken(candidate.attrs.material ?? ""));
    case "wood":
      return values.includes(normalizeToken(candidate.attrs.wood ?? ""));
    case "color":
    case "accessories":
    case "none":
      return false;
    default: {
      const _exhaustive: never = attribute;
      return _exhaustive;
    }
  }
};

const matchesHardFilter = (candidate: BeraterCandidate, attribute: BeraterAttribute, values: string[]): boolean => {
  if (!values.length) return true;
  if (NON_MATCHING_ATTRIBUTES.includes(attribute)) return true;
  return attributeMatches(candidate, attribute, values);
};

const scoreCandidate = (candidate: BeraterCandidate, answers: BeraterAnswer[], stepsByHandle: Map<string, BeraterStepData>): number => {
  let score = 0;
  for (const answer of answers) {
    const step = stepsByHandle.get(answer.stepHandle);
    if (!step || step.matchMode !== "soft") continue;
    if (NON_MATCHING_ATTRIBUTES.includes(step.attribute)) continue;
    const values = answer.values.map(normalizeToken);
    if (!values.length) continue;
    if (attributeMatches(candidate, step.attribute, values)) {
      score += step.weight;
    }
  }
  return score;
};

const filterCandidates = (
  pool: BeraterCandidate[],
  answers: BeraterAnswer[],
  steps: BeraterStepData[],
  relaxedAttributes: BeraterAttribute[] = [],
): BeraterCandidate[] => {
  const hardAttributes = new Set<BeraterAttribute>();
  for (const step of steps) {
    if (step.matchMode === "hard" && !NON_MATCHING_ATTRIBUTES.includes(step.attribute)) {
      hardAttributes.add(step.attribute);
    }
  }
  for (const attribute of ALWAYS_HARD_ATTRIBUTES) {
    hardAttributes.add(attribute);
  }

  return pool.filter((candidate) => {
    for (const attribute of hardAttributes) {
      if (relaxedAttributes.includes(attribute)) continue;
      const values = getAnswerValues(answers, attribute);
      if (!matchesHardFilter(candidate, attribute, values)) return false;
    }
    return true;
  });
};

const sortCandidates = (
  pool: BeraterCandidate[],
  answers: BeraterAnswer[],
  stepsByHandle: Map<string, BeraterStepData>,
): BeraterCandidate[] => {
  const wantColors = getAnswerValues(answers, "color");
  const wantWoods = getAnswerValues(answers, "wood");
  const wantSizes = getAnswerValues(answers, "size");
  const hasLinePreference =
    getDesignPreference(answers) !== null || getStrebenPreference(answers) !== null || getHeightPreference(answers) !== null;

  return [...pool].sort((a, b) => {
    if (wantColors.length) {
      const rankDiff = colorMatchRank(b, wantColors) - colorMatchRank(a, wantColors);
      if (rankDiff !== 0) return rankDiff;
      const handleRankDiff = colorHandleRank(b, wantColors) - colorHandleRank(a, wantColors);
      if (handleRankDiff !== 0) return handleRankDiff;
    }

    if (hasLinePreference) {
      const lineRankDiff = lineMatchRank(b, answers) - lineMatchRank(a, answers);
      if (lineRankDiff !== 0) return lineRankDiff;
    }

    if (wantSizes.length) {
      const sizeRankDiff = sizeMatchRank(b, wantSizes) - sizeMatchRank(a, wantSizes);
      if (sizeRankDiff !== 0) return sizeRankDiff;
    }

    if (wantWoods.length) {
      const woodRankDiff = woodMatchRank(b, wantWoods) - woodMatchRank(a, wantWoods);
      if (woodRankDiff !== 0) return woodRankDiff;
    }

    const scoreDiff = scoreCandidate(b, answers, stepsByHandle) - scoreCandidate(a, answers, stepsByHandle);
    if (scoreDiff !== 0) return scoreDiff;
    if (a.available !== b.available) return a.available ? -1 : 1;
    return a.priceMin - b.priceMin;
  });
};

const parseRelaxOrder = (data: BeraterData): BeraterAttribute[] => {
  const order: BeraterAttribute[] = [];
  for (const raw of data.relaxOrder) {
    const trimmed = raw.trim().toLowerCase();
    const canonical = RELAXABLE_BY_TOKEN.get(trimmed) ?? RELAXABLE_BY_TOKEN.get(normalizeToken(trimmed));
    if (!canonical) continue;
    if (HARD_NON_RELAXABLE.includes(canonical)) continue;
    if (!order.includes(canonical)) order.push(canonical);
  }
  return order;
};

export const getRecommendations = (data: BeraterData, answers: BeraterAnswer[]): BeraterRecommendation[] => {
  const pool = prepareCandidates(data.candidates);
  if (!pool.length) return [];

  const stepsByHandle = new Map(data.steps.map((step) => [step.handle, step]));
  let relaxed: BeraterAttribute[] = [];
  let matches = filterCandidates(pool, answers, data.steps, relaxed);
  const relaxOrder = parseRelaxOrder(data);

  while (!matches.length && relaxOrder.length) {
    const nextRelax = relaxOrder.find((attribute) => !relaxed.includes(attribute));
    if (!nextRelax) break;
    relaxed = [...relaxed, nextRelax];
    matches = filterCandidates(pool, answers, data.steps, relaxed);
  }

  matches = applyContextualFilters(matches, answers, relaxed);
  matches = applyPreferenceFilters(matches, answers);

  const sorted = sortCandidates(matches, answers, stepsByHandle);
  const droppedCriteria = relaxed.map((attribute) => attribute);

  return sorted.slice(0, 3).map((candidate) => ({
    handle: candidate.handle,
    relaxed: droppedCriteria.length > 0,
    droppedCriteria,
  }));
};

export const validateGraph = (data: BeraterData): string[] => {
  const errors: string[] = [];
  const handles = new Set(data.steps.map((step) => step.handle));
  const byHandle = new Map(data.steps.map((step) => [step.handle, step]));

  if (data.firstStep && !handles.has(data.firstStep)) {
    errors.push(`first_step "${data.firstStep}" not in steps list`);
  }

  for (const step of data.steps) {
    if (step.defaultNext && !handles.has(step.defaultNext)) {
      errors.push(`default_next "${step.defaultNext}" on "${step.handle}" missing`);
    }
    for (const option of step.options) {
      if (option.nextStep && !handles.has(option.nextStep)) {
        errors.push(`next_step "${option.nextStep}" on "${step.handle}" missing`);
      }
    }
  }

  if (data.firstStep && handles.has(data.firstStep)) {
    const reachable = new Set<string>();
    const queue = [data.firstStep];
    while (queue.length) {
      const handle = queue.shift() as string;
      if (reachable.has(handle)) continue;
      reachable.add(handle);
      const step = byHandle.get(handle);
      if (!step) continue;
      if (step.defaultNext) queue.push(step.defaultNext);
      for (const option of step.options) {
        if (option.nextStep) queue.push(option.nextStep);
      }
    }
    for (const step of data.steps) {
      if (!reachable.has(step.handle)) {
        errors.push(`step "${step.handle}" unreachable from first_step`);
      }
    }
  }

  return errors;
};
